import { z } from "zod";
// =============================================================================
// Базовые валидаторы
// =============================================================================
const trimmedNonEmpty = (max = 500) => z.string().trim().min(1, "Поле обязательно").max(max);
const optionalText = (max = 2000) => z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v && v.length > 0 ? v : undefined));
const contactRequired = trimmedNonEmpty(255);
// =============================================================================
// Сценарий 1. Advisory Board
// =============================================================================
export const advisoryBoardSchema = z.object({
    name: trimmedNonEmpty(255),
    contact: contactRequired,
    business: trimmedNonEmpty(1000), // ниша + месячный оборот
    goal: trimmedNonEmpty(2000),
});
// =============================================================================
// Сценарий 2. Эвент-консалтинг
// =============================================================================
export const EVENT_FORMATS = ["LAUNCH", "PARTNERSHIP", "CONSULTATION"];
export const eventConsultingSchema = z.object({
    name: trimmedNonEmpty(255),
    contact: contactRequired,
    format: z.enum(EVENT_FORMATS),
    dateAndScale: trimmedNonEmpty(500), // планируемая дата и масштаб
    needHelpWith: trimmedNonEmpty(2000),
});
// =============================================================================
// Сценарий 3. Маркетинг — разовая консультация
// =============================================================================
export const marketingConsultationSchema = z.object({
    name: trimmedNonEmpty(255),
    contact: contactRequired,
    niche: trimmedNonEmpty(500),
    monthlyRevenue: trimmedNonEmpty(255),
    request: trimmedNonEmpty(2000),
});
// =============================================================================
// Сценарий 4. Маркетинг — стратегия по SOSTAC (та же форма что и консультация)
// =============================================================================
export const marketingStrategySchema = marketingConsultationSchema;
// =============================================================================
// Сценарий 5. ПРОЕКТ 2.0 (тайм-менеджмент)
// =============================================================================
export const TIME_MGMT_FORMATS = ["IN_PERSON", "ZOOM"];
export const timeManagementSchema = z.object({
    name: trimmedNonEmpty(255),
    contact: contactRequired,
    format: z.enum(TIME_MGMT_FORMATS),
    inPersonLocation: optionalText(255), // если выбран IN_PERSON — где
});
// =============================================================================
// Сценарий 6. Автоматизация бизнеса
// =============================================================================
export const automationSchema = z.object({
    name: trimmedNonEmpty(255),
    contact: contactRequired,
    processDescription: trimmedNonEmpty(3000),
    expectedResult: trimmedNonEmpty(2000),
    deadline: trimmedNonEmpty(255),
});
// =============================================================================
// Сценарий 7. RESIDE — инвестиции
// =============================================================================
export const RESIDE_TYPES = ["APARTMENTS", "VILLA", "TOWNHOUSE", "UNDECIDED"];
export const RESIDE_STRATEGIES = ["LOW_RISK", "MODERATE", "HIGH_RISK", "UNDECIDED"];
export const RESIDE_PURPOSES = ["INVESTMENT_ONLY", "LIVING", "MIXED"];
export const RESIDE_READINESS = ["READY", "UNDER_CONSTRUCTION", "ANY"];
export const RESIDE_BUDGETS = ["UP_100K", "UP_150K", "UP_200K", "UP_500K", "OVER_500K"];
export const RESIDE_TIMELINES = ["MONTHS", "YEAR", "STUDYING"];
export const RESIDE_CHANNELS = ["WHATSAPP", "TELEGRAM", "VIBER"];
export const resideInvestmentSchema = z.object({
    propertyType: z.enum(RESIDE_TYPES),
    strategy: z.enum(RESIDE_STRATEGIES),
    purpose: z.enum(RESIDE_PURPOSES),
    readiness: z.enum(RESIDE_READINESS),
    budget: z.enum(RESIDE_BUDGETS),
    timeline: z.enum(RESIDE_TIMELINES),
    name: trimmedNonEmpty(255),
    contact: contactRequired,
    channel: z.enum(RESIDE_CHANNELS),
});
// =============================================================================
// Сценарий 8. Аренда виллы (партнёрский продукт с Ирой)
// =============================================================================
export const VILLA_LOCATIONS = ["SEMINYAK", "ULUWATU", "CANGGU", "UBUD", "UNDECIDED"];
export const VILLA_BUDGETS = ["UP_2K", "BTW_2_5K", "BTW_5_10K", "OVER_10K"];
export const villaRentSchema = z.object({
    name: trimmedNonEmpty(255),
    contact: contactRequired,
    location: z.enum(VILLA_LOCATIONS),
    checkIn: trimmedNonEmpty(64), // YYYY-MM-DD или произвольная строка
    checkOut: trimmedNonEmpty(64),
    adults: z.coerce.number().int().min(1).max(50),
    kids: z.coerce.number().int().min(0).max(50),
    bedrooms: z.coerce.number().int().min(1).max(20),
    budget: z.enum(VILLA_BUDGETS),
    specialRequests: optionalText(2000),
});
// =============================================================================
// Сценарий 9. Cocoon Bali — акционерство
// =============================================================================
export const COCOON_PACKAGES = ["STARTER", "PARTNER", "CO_INVESTOR", "CUSTOM", "EXPLORING"];
export const COCOON_TIMELINES = ["NOW", "MONTHS", "STUDYING"];
export const COCOON_EXPERIENCE = ["FIRST_TIME", "FEW_PROJECTS", "EXPERIENCED"];
export const cocoonInvestmentSchema = z.object({
    name: trimmedNonEmpty(255),
    contact: contactRequired,
    package: z.enum(COCOON_PACKAGES),
    timeline: z.enum(COCOON_TIMELINES),
    experience: z.enum(COCOON_EXPERIENCE),
});
// =============================================================================
// Сценарий 10. Реклама в Instagram
// =============================================================================
export const ADS_TYPES = ["ONE_TIME", "CONTRACT"];
export const instagramAdsSchema = z.object({
    name: trimmedNonEmpty(255),
    contact: contactRequired,
    instagram: trimmedNonEmpty(255),
    whatToAdvertise: trimmedNonEmpty(2000),
    expectedResult: trimmedNonEmpty(500),
    type: z.enum(ADS_TYPES),
});
// =============================================================================
// Сценарий 11. Партнёрство
// =============================================================================
export const partnershipSchema = z.object({
    name: trimmedNonEmpty(255),
    contact: contactRequired,
    proposal: trimmedNonEmpty(5000),
});
// =============================================================================
// Реестр всех схем — для бэка и фронта
// =============================================================================
export const payloadSchemas = {
    ADVISORY_BOARD: advisoryBoardSchema,
    EVENT_CONSULTING: eventConsultingSchema,
    MARKETING_CONSULTATION: marketingConsultationSchema,
    MARKETING_STRATEGY: marketingStrategySchema,
    TIME_MANAGEMENT: timeManagementSchema,
    AUTOMATION: automationSchema,
    RESIDE_INVESTMENT: resideInvestmentSchema,
    VILLA_RENT: villaRentSchema,
    COCOON_INVESTMENT: cocoonInvestmentSchema,
    INSTAGRAM_ADS: instagramAdsSchema,
    PARTNERSHIP: partnershipSchema,
};
//# sourceMappingURL=schemas.js.map